import React, { type FC, useEffect } from 'react'

import { type IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAtom } from 'jotai'
import { evalCondition } from 'mailup-react-helpers'
import {
  Button,
  BUTTON_SIZE_LG,
  BUTTON_VARIANT_PRIMARY,
  ContextMenu,
  Flex,
  makeButtonWithMenu,
  PageHeader,
} from 'mailup-web-components'
import { FilterType } from 'Types/beFiltersApi'

import setMenuItem from 'Helpers/setMenuItem'
import { faChevronDown } from 'mailup-fontawesome-kit/classic/regular'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { pageState } from 'State/page'
import { routesAccessConditions } from '../../../config/config'
import { availFilterTypesAtom } from '../state/state'
import { CombinedFilterAddButton } from './CombinedFilterAddButton'

const ButtonMenuComponent: FC = () => {
  const intl = useIntl()
  return (
    <Button
      kind={BUTTON_VARIANT_PRIMARY}
      size={BUTTON_SIZE_LG}
      data-mu-test='filter__cta-new'
    >
      <Flex alignItems='center' gap='8px'>
        <span>{intl.formatMessage({ id: 'Load.FiltersList.AddFilter' })}</span>

        <FontAwesomeIcon icon={faChevronDown as IconProp} />
      </Flex>
    </Button>
  )
}

const MenuComponent: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [availFilters] = useAtom(availFilterTypesAtom)
  const af = availFilters.map((f) => f.value)

  const items = [
    af.includes(FilterType.Anagraphic)
      ? {
          label: intl.formatMessage({
            id: 'Load.FiltersList.AnagraphicFilter',
          }),
          'data-mu-test': 'cta-new-anagraphic-filter',
          onClick: () => {
            navigate('/form-profile-filter/0')
          },
        }
      : undefined,

    af.includes(FilterType.Activity)
      ? {
          label: intl.formatMessage({
            id: 'Load.FiltersList.ActivityFilter',
          }),
          'data-mu-test': 'cta-new-activity-filter',
          onClick: () => {
            navigate('/form-activity-filter/0')
          },
        }
      : undefined,

    af.includes(FilterType.Engagement)
      ? {
          label: intl.formatMessage({
            id: 'Load.FiltersList.EngagementFilter',
          }),
          'data-mu-test': 'cta-new-engagement-filter',
          onClick: () => {
            navigate('/form-engagement-filter/0')
          },
        }
      : undefined,

    af.includes(FilterType.Geolocation)
      ? {
          label: intl.formatMessage({
            id: 'Load.FiltersList.GeolocationFilter',
          }),
          'data-mu-test': 'cta-new-geolocation-filter',
          onClick: () => {
            navigate('/form-geolocation-filter/0')
          },
        }
      : undefined,
  ].filter((o) => o !== undefined)

  return <ContextMenu items={items} />
}

const Header: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [{ status }] = useAtom(pageState)

  useEffect(() => {
    setMenuItem('ProfileFilters')
  }, [])

  const canAdd = evalCondition(routesAccessConditions.newFilter) as boolean

  const CTA = CombinedFilterAddButton

  const breadcrumbsItems = [
    {
      label: intl.formatMessage({ id: 'Load.NavMenu.Recipients' }),
      'data-mu-test': 'breadcrumb-item__recipients',
      onClick: Function.prototype,
    },
    {
      label: intl.formatMessage({ id: 'Load.NavMenu.RecipientsFilters' }),
      'data-mu-test': 'breadcrumb-item__filters',
      url: '#',
      onClick: () => {
        navigate('/filters/list')
      },
    },
    {
      label: intl.formatMessage({ id: 'Load.NavMenu.FiltersList' }),
      'data-mu-test': 'breadcrumb-item__list',
      onClick: Function.prototype,
    },
  ]

  const menuItems = [
    {
      label: intl.formatMessage({ id: 'Load.NavMenu.FiltersList' }),
      'data-mu-test': 'topmenu-item__filters',
      isActive: true,
    },
    {
      label: intl.formatMessage({
        id: 'Load.NavMenu.FiltersCategories',
      }),
      'data-mu-test': 'topmenu-item__categories',
      onClick: () => {
        navigate('/categories')
      },
    },
  ]

  const ControlsComponent =
    (status === 'ready' || status === 'fetching') && canAdd ? CTA : undefined

  return (
    <PageHeader
      title={intl.formatMessage({ id: 'Load.NavMenu.RecipientsFilters' })}
      breadcrumbsItems={breadcrumbsItems}
      menuItems={menuItems}
      ControlsComponent={ControlsComponent}
    />
  )
}

export default Header
